import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { API } from "@/lib/api";
import DataTable, { downloadBlob } from "@/components/DataTable";
import CommercialStatusBadge from "@/components/CommercialStatusBadge";

export default function SupplierPoList() {
  const nav = useNavigate();
  const [suppliers, setSuppliers] = useState([]);

  useEffect(() => { api.get("/suppliers").then((r) => setSuppliers(r.data || [])); }, []);

  const fetchData = React.useCallback(async ({ skip, limit, sort, search, filters }) => {
    const params = { skip, limit, sort, search };
    if (filters.status) params.status = filters.status;
    if (filters.supplier_id) params.supplier_id = filters.supplier_id;
    const { data } = await api.get("/supplier-pos", { params });
    return data;
  }, []);

  const columns = useMemo(() => [
    { key: "sp_number", label: "SP #", sortable: true,
      render: (r) => <span className="font-mono text-[12px] font-semibold text-blue-900">{r.sp_number}</span> },
    { key: "sp_date", label: "Date", sortable: true, className: "font-mono text-xs text-slate-600" },
    { key: "supplier_name", label: "Supplier", className: "text-slate-800" },
    { key: "scope", label: "Scope",
      render: (r) => <span className="text-xs text-slate-700">{r.scope || "—"}</span> },
    { key: "oa_number", label: "From OA",
      render: (r) => <span className="font-mono text-xs text-slate-600">{r.oa_number || "—"}</span> },
    { key: "customer_name", label: "End Customer",
      render: (r) => <span className="text-xs text-slate-700">{r.customer_name || "—"}</span> },
    { key: "line_count", label: "Lines",
      render: (r) => <span className="font-mono text-xs">{r.line_count}</span> },
    { key: "total_qty", label: "Total Qty",
      render: (r) => <span className="font-mono text-xs font-semibold">{r.total_qty}</span> },
    { key: "total_value", label: "Value ₹", visibleByDefault: false,
      render: (r) => <span className="font-mono text-xs font-semibold">₹{(r.total_value || 0).toLocaleString("en-IN")}</span> },
    { key: "status", label: "Status", sortable: true,
      render: (r) => <CommercialStatusBadge status={r.status} /> },
  ], []);

  return (
    <div data-testid="supplier-po-list-page" className="space-y-4">
      <div>
        <div className="text-[10px] uppercase tracking-widest text-red-600 font-semibold">Sub-Contract · Supplier POs</div>
        <h1 className="text-3xl font-bold text-slate-900 mt-1" style={{ fontFamily: "Chivo, sans-serif" }}>Supplier Purchase Orders</h1>
        <p className="text-sm text-slate-500 mt-1">
          Sub-contract purchase orders spawned from Order Acceptances. Generate any new SP from the source OA using
          <span className="font-mono"> · Convert to Supplier PO ·</span>
        </p>
      </div>

      <DataTable
        columns={columns} fetchData={fetchData}
        filters={[
          { key: "status", label: "Status",
            options: ["Draft", "Sent", "Acknowledged", "PartiallyReceived", "Received", "Cancelled", "Closed"].map((s) => ({ value: s, label: s })) },
          { key: "supplier_id", label: "Supplier",
            options: suppliers.map((c) => ({ value: c.id, label: c.name })) },
        ]}
        storageKey="supplier-pos"
        onRowClick={(r) => nav(`/supplier-pos/${r.id}`)}
        emptyMessage="No supplier POs yet. Open an OA and click ‘Convert to Supplier PO’ to generate one."
      />
    </div>
  );
}
